import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { handleRemoveEmptyParamFromQueryParams } from "../../../utils/helpers";
import {
    IPaginatedApiResponse,
    IApiResponse,
} from "../../../utils/types/api-response";
import {
    fetchCardTransactionHistory,
    fetchClientCreditCards,
    fetchClientDocuments,
    fetchClientInsuranceHistory,
    fetchClientNotes,
    fetchClientMemos,
    fetchClientProviderHistory,
    fetchInsuranceProviders,
    fetchClientFees,
    fetchClientFlags,
    fetchClientTags,
    fetchClientTagActivities,
    exportClientsList,
    fetchNormalizedEmployerNames,
    fetchClientInsuranceFees,
    fetchClientSmsHistory,
    fetchClientCallHistory,
    fetchCollectionsNotes,
    fetchCollectionsNoteActivities,
} from "../services/clients.services";
import {
    ICallHistoryFilters,
    ICardFilterTypes,
    ICardHistory,
    IClientCallLog,
    IClientSmsMessage,
    ISmsHistoryFilters,
    IClientCreditCardDetails,
    IClientDocument,
    IClientDocumentsListFilters,
    IClientInsuranceDetails,
    IClientInsuranceProvider,
    IClientNote,
    IClientNotesListFilterTypes,
    IClientMemo,
    IClientMemoListFilterTypes,
    ICollectionsNote,
    ICollectionsNoteActivity,
    ICollectionsNoteFilterTypes,
    IClientProviderHistory,
    IClientProviderHistoryListFilters,
    IClientFee,
    IClientFlag,
    IInsuranceProvidersFilterTypes,
    IClientInsuranceRecords,
    IClientTag,
    IClientTagActivity,
    IClientTagActivityFilterTypes,
} from "../types/clients.types";
import { UserPermisions } from "../../../utils/types/user";
import { useDoesUserHavePermission } from "../../../hooks/permissions";
import { IClientFilterTypes } from "../../../utils/types/client";

// Fetch card transaction history
export function useFetchCardTransactionHistory(
    clientId: string,
    paymentId: string,
    filters: ICardFilterTypes
) {
    return useQuery<
        IPaginatedApiResponse<ICardHistory[]>,
        AxiosError<IApiResponse<ICardHistory[]>>
    >({
        queryKey: [
            "card_history",
            handleRemoveEmptyParamFromQueryParams(filters),
            paymentId,
        ],
        queryFn: () =>
            fetchCardTransactionHistory(
                clientId,
                paymentId,
                handleRemoveEmptyParamFromQueryParams(filters)
            ),
    });
}

// Fetch client credit cards list hook
export function useFetchClientCreditCards(clientId: string) {
    return useQuery<
        IPaginatedApiResponse<IClientCreditCardDetails[]>,
        AxiosError<IApiResponse<IClientCreditCardDetails[]>>
    >({
        queryKey: [clientId, "cards"],
        queryFn: () => fetchClientCreditCards(clientId),
    });
}

// Fetch client documents list hook
export function useFetchDocuments(filters: IClientDocumentsListFilters) {
    return useQuery<
        IPaginatedApiResponse<IClientDocument[]>,
        AxiosError<IApiResponse<IClientDocument[]>>
    >({
        queryKey: [filters.client_id, `documents`, filters],
        queryFn: () => fetchClientDocuments(filters),
    });
}

// Fetch insurance providers list
export function useFetchInsuranceProviders(
    filters?: IInsuranceProvidersFilterTypes
) {
    const canViewInsuranceProvidersList = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_INSURANCE_READ,
        UserPermisions.CLIENT_INFO_INSURANCE_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    return useQuery<
        IApiResponse<IClientInsuranceProvider[]>,
        AxiosError<IApiResponse<IClientInsuranceProvider>>
    >({
        queryKey: [
            "insurance-providers",
            handleRemoveEmptyParamFromQueryParams(filters || {}),
        ],
        queryFn: () => fetchInsuranceProviders(filters),
        enabled: canViewInsuranceProvidersList,
    });
}

// Fetch client insurance history hook
export function useFetchClientInsuranceHistory(
    clientId: string,
    page: number,
    limit: number,
    enabled?: boolean
) {
    return useQuery<
        IPaginatedApiResponse<IClientInsuranceDetails[]>,
        AxiosError<IApiResponse<IClientInsuranceDetails[]>>
    >({
        queryKey: [clientId, `insurance`, page, limit],
        queryFn: () => fetchClientInsuranceHistory(clientId, page, limit),
        enabled,
    });
}

export function useFetchClientProviderHistory(
    clientId: string,
    filters: IClientProviderHistoryListFilters
) {
    return useQuery<
        IPaginatedApiResponse<IClientProviderHistory[]>,
        AxiosError<IApiResponse<IClientProviderHistory[]>>
    >({
        queryKey: [
            clientId,
            "provider-history",
            handleRemoveEmptyParamFromQueryParams({
                limit: filters.limit,
                page: filters.page,
            }),
        ],
        queryFn: () => fetchClientProviderHistory(clientId, filters),
    });
}

// Fetch client notes list hook
export function useFetchClientNotes(
    clientId: string,
    filters: IClientNotesListFilterTypes
) {
    return useQuery<
        IPaginatedApiResponse<IClientNote[]>,
        AxiosError<IApiResponse<IClientNote[]>>
    >({
        queryKey: [
            clientId,
            "notes",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => fetchClientNotes(clientId, filters),
    });
}

export function useFetchClientMemos(
    clientId: string,
    filters: IClientMemoListFilterTypes
) {
    return useQuery<
        IPaginatedApiResponse<IClientMemo[]>,
        AxiosError<IApiResponse<IClientMemo[]>>
    >({
        queryKey: [
            clientId,
            `memos`,
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => fetchClientMemos(clientId, filters),
    });
}

export function useFetchCollectionsNotes(
    clientId: string,
    filters: ICollectionsNoteFilterTypes,
    enabled?: boolean
) {
    return useQuery<
        IPaginatedApiResponse<ICollectionsNote[]>,
        AxiosError<IApiResponse<ICollectionsNote[]>>
    >({
        queryKey: [
            clientId,
            "collections-notes",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => fetchCollectionsNotes(clientId, filters),
        enabled,
    });
}

export function useFetchCollectionsNoteActivities(
    clientId: string,
    filters: ICollectionsNoteFilterTypes,
    enabled: boolean
) {
    return useQuery<
        IPaginatedApiResponse<ICollectionsNoteActivity[]>,
        AxiosError<IApiResponse<ICollectionsNoteActivity[]>>
    >({
        queryKey: [
            clientId,
            "collections-notes",
            "activities",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => fetchCollectionsNoteActivities(clientId, filters),
        enabled,
    });
}

export function useFetchClientFees(
    clientId: string,
    limit: number,
    enabled?: boolean
) {
    return useQuery<
        IPaginatedApiResponse<IClientFee[]>,
        AxiosError<IApiResponse<IClientFee[]>>
    >({
        queryKey: [clientId, `fees`, limit],
        queryFn: () => fetchClientFees(clientId, limit),
        enabled,
    });
}

// Hook for fetching client flags
export function useFetchClientFlags(clientId: string, enabled: boolean) {
    return useQuery<
        IPaginatedApiResponse<IClientFlag[]>,
        AxiosError<IApiResponse<IClientFlag[]>>
    >({
        queryKey: [clientId, `client-flags`],
        queryFn: () => fetchClientFlags(clientId),
        enabled,
    });
}

// Hook for fetching client tags
export function useFetchClientTags(clientId: string, enabled: boolean) {
    return useQuery<
        IApiResponse<IClientTag[]>,
        AxiosError<IApiResponse<IClientTag[]>>
    >({
        queryKey: [clientId, "client-tags"],
        queryFn: () => fetchClientTags(clientId),
        enabled,
    });
}

// Hook for fetching client tag activities
export function useFetchClientTagActivities(
    clientId: string,
    filters: IClientTagActivityFilterTypes,
    enabled: boolean
) {
    return useQuery<
        IPaginatedApiResponse<IClientTagActivity[]>,
        AxiosError<IApiResponse<IClientTagActivity[]>>
    >({
        queryKey: [
            clientId,
            "client-tag-activities",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => fetchClientTagActivities(clientId, filters),
        enabled,
    });
}

export function useExportClientsList(
    filters: IClientFilterTypes,
    enabled: boolean
) {
    return useQuery<Blob, AxiosError<string>>({
        queryKey: [
            "export-clients",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => exportClientsList(filters),
        onSuccess: (data) => {
            const href = window.URL.createObjectURL(data);

            const anchorElement = document.createElement("a");
            anchorElement.href = href;
            anchorElement.download = `Client-list-export.xlsx`;

            document.body.appendChild(anchorElement);
            anchorElement.click();

            document.body.removeChild(anchorElement);
            window.URL.revokeObjectURL(href);
        },
        enabled,
    });
}

export function useFetchNormalizedEmployerNames(
    searchString?: string,
    includeInactive?: boolean
) {
    return useQuery<IApiResponse<string[]>, AxiosError<IApiResponse<string[]>>>(
        {
            queryKey: [
                "normalized-employer-names",
                searchString,
                includeInactive,
            ],
            queryFn: () =>
                fetchNormalizedEmployerNames(searchString, includeInactive),
        }
    );
}

export function useFetchClientInsuranceFees(
    clientId: string,
    page?: number,
    limit?: number
) {
    return useQuery<
        IPaginatedApiResponse<IClientInsuranceRecords[]>,
        AxiosError<IApiResponse<IClientInsuranceRecords[]>>
    >({
        queryKey: [
            clientId,
            `insurance-fees`,
            handleRemoveEmptyParamFromQueryParams({ page, limit }),
        ],
        queryFn: () => fetchClientInsuranceFees(clientId, page, limit),
    });
}

export function useFetchClientSmsHistory(
    clientId: string,
    filters: ISmsHistoryFilters
) {
    return useQuery<
        IPaginatedApiResponse<IClientSmsMessage[]>,
        AxiosError<IApiResponse<IClientSmsMessage[]>>
    >({
        queryKey: [
            clientId,
            "sms-history",
            handleRemoveEmptyParamFromQueryParams({ ...filters }),
        ],
        queryFn: () => fetchClientSmsHistory(clientId, filters),
    });
}

export function useFetchClientCallHistory(
    clientId: string,
    filters: ICallHistoryFilters
) {
    return useQuery<
        IPaginatedApiResponse<IClientCallLog[]>,
        AxiosError<IApiResponse<IClientCallLog[]>>
    >({
        queryKey: [
            clientId,
            "call-history",
            handleRemoveEmptyParamFromQueryParams({ ...filters }),
        ],
        queryFn: () => fetchClientCallHistory(clientId, filters),
    });
}
